import { watchDeep } from '@vueuse/core';
import { computed, ref } from 'vue';

import { useDisplayMedia } from '@vueuse/core/index';
import { defineStore } from 'pinia';

import { i18n } from '@/plugins/i18n';
import { useToastStore } from '@/stores/toast.js';
import { Nullable } from '@/types/common';
import { BUFFER_SIZE, SAMPLE_RATE, MIC_INITIAL_GAIN } from '@/utils/constants/microphone';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import getSocketId from '@/utils/getSocketId';
import runQueue from '@/utils/useQueue';

export const useSystemAudioMicrophoneStore = defineStore('systemAudioMicrophone', () => {
  const toastStore = useToastStore();
  const t = i18n.global.t;

  /* states */
  // 마이크 노드
  const micSourceNode = ref<Nullable<MediaStreamAudioSourceNode>>(null); // 마이크 소스 노드
  const micGain = ref<Nullable<GainNode>>(null); // 마이크 감도
  const micAnalyser = ref<Nullable<AnalyserNode>>(null); // 마이크 분석기
  const micContext = ref<Nullable<AudioContext>>(null); // 마이크 컨테스트
  const micProcessor = ref<Nullable<AudioWorkletNode>>(null); // 마이크 프로세서
  const socketId = ref<string>('');

  // 마이크 이벤트
  const micEvent = ref<Nullable<Event>>(); // 마이크 입출력 이벤트
  const micErrorEvent = ref<Nullable<Event>>(); // 마이크 입출력 이벤트

  // 마이크 그래프 큐
  const micGraphRenderQueue = runQueue();

  /* getters */
  /**
   * constraints
   * https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API/Constraints
   * */
  const audioConstraints = computed(() => {
    const { os } = getBrowserAndDeviceInfo();
    return {
      echoCancellation: false,
      noiseSuppression: os === 'ios',
      autoGainControl: false,
      channelCount: 1,
    };
  });

  const {
    enabled: isMicOn,
    stream,
    start,
    stop,
  } = useDisplayMedia({
    video: {
      // 최소한의 비디오 설정 (ch오디오만 필요하므로)
      width: 1,
      height: 1,
      frameRate: 1,
    },
    audio: audioConstraints.value,
  });

  const intiSystemAudioMicrophoneStore = async () => {
    socketId.value = '';
    initMicEvent();
    initMicErrorEvent();
    await stopMic();
  };

  /* actions */
  const initSocketId = () => {
    socketId.value = getSocketId();
  };

  const initMicEvent = (event?: Event) => {
    if (event) {
      micEvent.value = event;
    } else {
      micEvent.value = null;
    }
  };

  const initMicErrorEvent = (event?: Event) => {
    if (event) {
      micErrorEvent.value = event;
    } else {
      micErrorEvent.value = null;
    }
  };

  /**
   * @desc 마이크 시작
   * */
  const startMic = async () => {
    await clearMic();
    const startMicWithQueue = async () => {
      try {
        await start();
        if (stream.value) {
          isMicOn.value = true;
          await setupMicGraph();
        } else {
          throw new Error();
        }
      } catch {
        toastStore.error(t('reject_mic'));
        if (micErrorEvent.value) {
          micErrorEvent.value();
        }
        await stopMic();
      }
    };
    micGraphRenderQueue.add(startMicWithQueue);
  };

  /**
   * @desc 마이크 일시 정지
   * */
  const stopMic = async () => {
    stop();
    await clearMic();
  };

  /**
   * @desc 오디오 트랙 및 오디오 그래프를 해제하고 모든 노드를 초기화
   * */
  const clearMic = async () => {
    const clearMicWithQueue = async () => {
      if (micProcessor.value && micProcessor.value.numberOfOutputs > 0) {
        micProcessor.value.disconnect();
        micProcessor.value.port.close();
        micProcessor.value = null;
      }

      if (micSourceNode.value && micSourceNode.value.numberOfOutputs > 0) {
        micSourceNode.value.disconnect();
        micSourceNode.value = null;
      }

      if (micGain.value && micGain.value.numberOfOutputs > 0) {
        micGain.value.disconnect();
        micGain.value = null;
      }

      if (micAnalyser.value && micAnalyser.value.numberOfOutputs > 0) {
        micAnalyser.value.disconnect();
        micAnalyser.value = null;
      }

      if (micContext.value && micContext.value.state !== 'closed') {
        try {
          await micContext.value.close();
        } finally {
          micContext.value = null;
        }
      }
    };

    micGraphRenderQueue.add(clearMicWithQueue);
  };

  /**
   * @desc 마이크 감도(게인)를 조절합니다.
   * */
  const controlMicGain = () => {
    if (micGain.value) {
      micGain.value.gain.value = MIC_INITIAL_GAIN;
    }
  };

  /**
   * @desc 마이크 오디오 노드 그래프를 구성하고 활성화합니다.
   * */
  const setupMicGraph = async () => {
    if (!stream.value) {
      return;
    }

    try {
      // Video Stream 종료
      stream.value.getVideoTracks().forEach((t) => t.stop());

      micContext.value = new AudioContext({
        sampleRate: SAMPLE_RATE,
      });

      // 01) 오디오 워크렛 모듈 등록
      const modulePath =
        import.meta.env.BASE_URL === '/' ? `/js/processor.js` : `${import.meta.env.BASE_URL}js/processor.js`;
      await micContext.value.audioWorklet.addModule(modulePath);

      // 02) 소스 노드 생성
      micSourceNode.value = micContext.value.createMediaStreamSource(stream.value);

      // 03) 게인 노드 생성
      micGain.value = micContext.value.createGain();
      controlMicGain();

      // 04) 분석기 노드 생성
      micAnalyser.value = micContext.value.createAnalyser();
      micAnalyser.value.fftSize = 1024;

      // 05) 프로세서 노드 생성
      micProcessor.value = new AudioWorkletNode(micContext.value, 'audio-processor', {
        processorOptions: { bufferSize: BUFFER_SIZE },
      });

      // 06) 노드 체인 연결
      micSourceNode.value
        .connect(micGain.value)
        .connect(micAnalyser.value)
        .connect(micProcessor.value)
        .connect(micContext.value.destination);

      // 07) 마이크 이벤트 설정
      if (micEvent.value) {
        micProcessor.value.port.onmessage = (e: MessageEvent<Float32Array>) => {
          if (isMicOn.value) {
            micEvent.value(e.data);
          }
        };
        micProcessor.value.port.onmessageerror = async () => {
          toastStore.warn(t('unstable_mic'));
          await stopMic();
        };
      }
    } catch {
      // 내부적인 로직에서 에러난경우
      toastStore.warn(t('error_mic'));
      await stopMic();
      if (micErrorEvent.value) {
        micErrorEvent.value();
      }
    }
  };

  /**
   * @desc isMicOn 상태가 변경될 때 토스트 메시지를 표시합니다.
   **/
  watchDeep(isMicOn, async (val) => {
    if (val) {
      toastStore.info(t('toast_mic_on'), 'MIC');
    } else {
      toastStore.info(t('toast_mic_off'), 'MIC');
    }
  });

  return {
    /* states */
    isMicOn, // 마이크 활성화 상태
    socketId,
    /* actions */
    intiSystemAudioMicrophoneStore,
    initSocketId,
    initMicErrorEvent, // 마이크 에러 이벤트 설정
    initMicEvent, // 마이크 이벤트 설정
    startMic, // 마이크 시작
    stopMic, // 마이크 중지
    controlMicGain, // 마이크 감도 조절
  };
});
